import FilterSearchBar from 'flow/components/FilterSearchBar';
import useAppContext from 'flow/hooks/useAppContext';
import React, { useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';

import { List as IList } from 'immutable';
import { extractName, isEmptyNullOrUndefined } from 'flow/utils';
import ContactAvatar from '../ui-components/ContactAvatar';
import Icon from '../ui-components/Icon';
import { Extension } from 'flow/types/App';
import { TelephoneNumber } from 'flow/types/TelephoneNumber';
import { TypedMap } from 'flow/types/TypedMap';
import { Contact, Mobile } from 'flow/types/Contact';

interface Props {
    handleSelect: (
        number: TypedMap<TelephoneNumber> | TypedMap<Mobile>,
    ) => void;
}

const RedirectPhoneNumberPicker = ({ handleSelect }: Props) => {
    const { i18next } = useAppContext();

    const [filterText, setFilterText] = useState('');

    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );
    const queues = useAppSelector((state) => state.pbx.get('queues'));
    const refers = useAppSelector((state) => state.pbx.get('refers'));

    const renderFixedOption = (contact: TypedMap<Contact>) => {
        const fixed = contact.get('fixed');

        if (!isEmptyNullOrUndefined(fixed)) {
            return (
                <button
                    className="flex cursor-pointer flex-row items-center gap-2 p-3"
                    onClick={() => handleSelect(contact.get('fixed'))}
                >
                    <ContactAvatar contact={contact} />
                    <div>
                        <p>{contact.get('firstName')}</p>
                        <p className="text-sm text-gray-500">
                            <Icon iconKey="mobile" size="1rem" />
                            {contact.getIn(['fixed', 'number'])}
                        </p>
                    </div>
                </button>
            );
        }
        return null;
    };

    const renderMobileOption = (contact: TypedMap<Contact>) => {
        const mobile = contact.get('mobile');

        if (!isEmptyNullOrUndefined(mobile)) {
            return (
                <button
                    className="flex cursor-pointer flex-row items-center gap-2 p-3"
                    onClick={() => handleSelect(contact.get('mobile'))}
                >
                    <ContactAvatar contact={contact} />
                    <div>
                        <p>{contact.get('firstName')}</p>
                        <div className="flex flex-row items-center gap-1 text-gray-500">
                            <Icon iconKey="phone" size="1rem" />
                            <p className="text-sm">
                                {contact.getIn(['mobile', 'number'])}
                            </p>
                        </div>
                    </div>
                </button>
            );
        }
        return null;
    };

    const renderExtension = (extension: TypedMap<Extension>) => {
        const contact = extension.get('contact');
        if (contact) {
            return React.Children.toArray([
                renderFixedOption(extension.get('contact')),
                renderMobileOption(extension.get('contact')),
            ]);
        }
        return null;
    };

    const createGenericExtension = (extensionDTO: any) => {
        const keyword = extractName(extensionDTO.get('contact'), '')
            .concat(extensionDTO.getIn(['contact', 'fixed', 'nationalFormat']))
            .concat(
                extensionDTO.getIn(['contact', 'mobile', 'nationalFormat']),
            );
        return {
            type: 'extension',
            data: extensionDTO,
            keyword,
        };
    };

    const createGenericQueue = (queueDTO: any) => {
        const keyword = queueDTO
            .get('description', '')
            .concat(queueDTO.getIn(['number', 'nationalFormat']));
        return {
            type: 'queue',
            data: queueDTO,
            keyword,
        };
    };

    const createGenericRefer = (referDTO: any) => {
        const keyword = referDTO
            .get('description', '')
            .concat(referDTO.getIn(['number', 'nationalFormat']));
        return {
            type: 'refer',
            data: referDTO,
            keyword,
        };
    };

    const genericExtensions = extensions
        ? extensions.toList().map(createGenericExtension)
        : IList();
    const genericQueues = queues
        ? queues.toList().map(createGenericQueue)
        : IList();
    const genericRefers = refers
        ? refers.toList().map(createGenericRefer)
        : IList();

    const sortedItems = genericExtensions
        .concat(genericQueues)
        .concat(genericRefers)
        .sort((item1: any, item2: any) => {
            return item1.keyword.localeCompare(item2.keyword, 'sv');
        });

    const filterItemsByKeyword = (items: any, filterText = '') => {
        const search = filterText.toLowerCase();
        return items.filter(
            (item: any) => item.keyword.toLowerCase().indexOf(search) !== -1,
        );
    };

    const filteredMembers = filterItemsByKeyword(sortedItems, filterText || '');

    return (
        <div>
            <FilterSearchBar
                searchText={filterText}
                placeholder={i18next.t('input.search')}
                handleFilter={(event, value) => {
                    setFilterText(value);
                }}
            />

            <div>
                {filteredMembers.map((item: any, idx: number) => (
                    <React.Fragment key={idx}>
                        {item.type === 'extension' &&
                            renderExtension(item.data)}

                        {item.type === 'queue' && (
                            <>
                                {item.data.getIn(['number', 'number']) && (
                                    <button
                                        className="flex cursor-pointer flex-row items-center gap-2 p-3"
                                        onClick={() =>
                                            handleSelect(item.get('number'))
                                        }
                                    >
                                        <Icon
                                            iconKey="queuelogin"
                                            size="2.5rem"
                                        />
                                        <div>
                                            <p>
                                                {item.data.get('description')}
                                            </p>
                                            <div className="flex flex-row items-center gap-1 text-gray-500">
                                                <Icon
                                                    iconKey="phone"
                                                    size="1rem"
                                                />
                                                <p className="text-sm">
                                                    {item.data.getIn([
                                                        'number',
                                                        'number',
                                                    ])}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                )}
                            </>
                        )}

                        {item.type === 'refer' && (
                            <>
                                {item.data.getIn(['number', 'number']) && (
                                    <button
                                        className="flex cursor-pointer flex-row items-center gap-2 p-3"
                                        onClick={() =>
                                            handleSelect(item.get('number'))
                                        }
                                    >
                                        <Icon iconKey="ivr" size="2.5rem" />
                                        <div>
                                            <p>
                                                {item.data.get('description')}
                                            </p>
                                            <div className="flex flex-row items-center gap-1 text-gray-500">
                                                <Icon
                                                    iconKey="phone"
                                                    size="1rem"
                                                />
                                                <p className="text-sm">
                                                    {item.data.getIn([
                                                        'number',
                                                        'number',
                                                    ])}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                )}
                            </>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

export default RedirectPhoneNumberPicker;
